/**
 * Hook per pescare info da football-data.org via backend.
 *
 * `useGiornataInfo(matchday)` → orario prima partita di una giornata Serie A.
 * Cache backend 10 min, frontend staleTime 5 min per evitare polling.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export interface GiornataInfo {
  matchday: number;
  prima_partita_at: string | null; // ISO 8601 UTC
  ultima_partita_at: string | null;
  num_partite: number;
  n_giocate: number;
  cached_at: string | null;
}

/** Info giornata Serie A da football-data (orario prima partita).
 *
 * NB: tier free football-data e' 10 req/min e ban del token se eccede.
 * L'orario della prima partita di una giornata e' fissato in anticipo e
 * cambia raramente → staleTime aggressivo 30 min, no refetchInterval.
 * Backend cache 10 min: la prima richiesta di un matchday va a football-data,
 * le successive (entro 10 min) escono dalla cache senza network call.
 */
export function useGiornataInfo(matchday: number | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["football-data-giornata", matchday],
    queryFn: async () => {
      const res = await api.get<GiornataInfo>(
        `/admin/football-data/giornata/${matchday}`,
      );
      return res.data;
    },
    enabled: isAuth && matchday !== null && matchday >= 1 && matchday <= 38,
    staleTime: 30 * 60 * 1000, // 30 min lato client
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false, // se 502 (API key mancante/rate limit) non ritentare
  });
}
